import { EFinancingProgram } from '@if/core/types'
import { ProductsComputeRequestDto } from '@src/data/types/ProductsComputeRequestDto'
import { ProductsComputeResultDto } from '@src/data/types/ProductsComputeResultDto'
import {
  ProductsWorksheet,
  ProductsWorksheetDecisionDTO,
  ProductsWorksheetDto,
  ProductsWorksheetRevision,
} from '@src/data/types/ProductsWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import transformProductsWorksheetToApi from './products-worksheet-transform'

const baseUrl = '/Applications/products/'
const worksheetUrl = `${baseUrl}{creditApplicationId}/Worksheet`
const calculatorUrl = `${baseUrl}Calculator/Compute`

// Query Keys
export const productsWorksheetKeys = {
  all: ['productsWorksheet'] as const,
  revisions: (creditApplicationId: string) => [...productsWorksheetKeys.all, 'revisions', creditApplicationId] as const,
}

// API Functions
export async function createProductsWorksheet(data: ProductsWorksheetDto): Promise<ProductsWorksheet> {
  const apiClient = getApiClient()
  const url = worksheetUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<ProductsWorksheet>(url, data)
  return response.data
}

export async function updateProductsWorksheet(data: ProductsWorksheetDto): Promise<ProductsWorksheet> {
  const apiClient = getApiClient()
  const url = worksheetUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const transformedData = transformProductsWorksheetToApi(data)
  const response = await apiClient.put<ProductsWorksheet>(url, transformedData)
  return response.data
}

export async function computeProductsLoan(data: ProductsComputeRequestDto): Promise<ProductsComputeResultDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<ProductsComputeResultDto>(calculatorUrl, data)
  return response.data
}

export async function updateProductsWorksheetDecision(data: ProductsWorksheetDecisionDTO): Promise<ProductsWorksheet> {
  const apiClient = getApiClient()
  const url = `${worksheetUrl}/Decision`.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<ProductsWorksheet>(url, data)
  return response.data
}

export async function getProductsWorksheetRevisions(creditApplicationId: string): Promise<ProductsWorksheetRevision[]> {
  const apiClient = getApiClient()
  const url = `${worksheetUrl}/Revisions`.replace('{creditApplicationId}', creditApplicationId)
  const response = await apiClient.get<ProductsWorksheetRevision[]>(url)
  return response.data
}

// React-Query Hooks
export function useGetProductsWorksheetRevisions(
  creditApplicationId: string | undefined,
): [ProductsWorksheetRevision[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: productsWorksheetKeys.revisions(creditApplicationId ?? ''),
    queryFn: () => getProductsWorksheetRevisions(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}

export function useCreateProductsWorksheet(): [MutationFunction<ProductsWorksheet, ProductsWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createProductsWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        createdWorksheet.creditApplicationId,
        EFinancingProgram.Products,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateProductsWorksheet(): [MutationFunction<ProductsWorksheet, ProductsWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateProductsWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        updatedWorksheet.creditApplicationId,
        EFinancingProgram.Products,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useComputeProductsLoan(): [
  MutationFunction<ProductsComputeResultDto, ProductsComputeRequestDto>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: computeProductsLoan,
  })

  return [mutateAsync, isPending]
}

export function useUpdateProductsWorksheetDecision(): [
  MutationFunction<ProductsWorksheet, ProductsWorksheetDecisionDTO>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateProductsWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        updatedWorksheet.creditApplicationId,
        EFinancingProgram.Products,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}
